import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Building2, Upload, Image as ImageIcon, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { logger } from '../utils/logger';
import DocumentBranding from './DocumentBranding';
import DocumentStyleCard from './DocumentStyleCard';
import ReportLayoutCard from './ReportLayoutCard';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const CompanyBranding = ({ company, token }) => {
  const { toast } = useToast();
  const { refreshCompanyData } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  // Track which logo URL failed to load. We derive `imageLoadError` from this
  // so the error auto-resets when the URL changes (avoids a setState-in-effect
  // anti-pattern and prevents the img from being unmounted, which would have
  // blocked the onLoad handler for the new URL).
  const [erroredUrl, setErroredUrl] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid File Type',
        description: 'Only PNG, JPG, SVG, GIF, and PDF files are allowed',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Logo must be under 2MB',
        variant: 'destructive'
      });
      return;
    }

    // Store file and show preview
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async () => {
    if (!selectedFile) {
      toast({
        title: 'No File Selected',
        description: 'Please select a logo file first',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API}/company/upload-logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({
        title: 'Success',
        description: 'Company logo saved successfully! Refreshing...',
        duration: 3000
      });

      // Refresh company data to get the updated logo
      if (refreshCompanyData) {
        await refreshCompanyData();
      }

      // Clear preview and selected file only after refresh completes
      setPreviewUrl(null);
      setSelectedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('logo-upload');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Logo upload error:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload logo. Please try again.',
        variant: 'destructive'
      });
      setPreviewUrl(null);
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    // Reset file input
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) fileInput.value = '';
  };

  const currentLogoUrl = company?.company_logo 
    ? `${API}/company/logo/${company.company_logo}`
    : null;

  const displayedLogoUrl = previewUrl || currentLogoUrl;
  const imageLoadError = displayedLogoUrl != null && erroredUrl === displayedLogoUrl;

  return (
    <>
    <Card className="border-2">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b">
        <CardTitle className="flex items-center space-x-2">
          <Building2 className="w-6 h-6 text-blue-600" />
          <span>Company Branding</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div>
            <p className="text-sm text-slate-400 mb-4">
              Upload your company logo to customize the Hines Time platform. Your logo will appear in the navigation header, emails, and reports.
            </p>
          </div>

          {/* Current or Preview Logo */}
          <div className="flex items-center justify-center p-8 bg-[#0F1923] rounded-lg border-2 border-dashed border-[#2D3748] min-h-[200px]">
            {(previewUrl || (currentLogoUrl && !imageLoadError)) ? (
              <div className="text-center w-full">
                {!imageLoadError && (
                  <img
                    src={previewUrl || currentLogoUrl}
                    alt="Company Logo"
                    className="max-h-32 max-w-full mx-auto mb-3 object-contain"
                    onLoad={() => {
                      logger.debug('Logo loaded successfully:', displayedLogoUrl);
                      setErroredUrl(null);
                    }}
                    onError={(e) => {
                      console.error('Logo failed to load:', displayedLogoUrl);
                      console.error('Error details:', e);
                      setErroredUrl(displayedLogoUrl);
                    }}
                  />
                )}
                {!imageLoadError ? (
                  <p className="text-sm font-medium text-white">
                    {previewUrl ? '✓ Preview - Click Save to apply' : '✓ Current Logo'}
                  </p>
                ) : (
                  <div className="text-center text-red-600">
                    <ImageIcon className="w-16 h-16 mx-auto mb-3" />
                    <p className="text-sm font-medium">Failed to load logo</p>
                    <p className="text-xs">Please try uploading again</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <ImageIcon className="w-16 h-16 mx-auto mb-3" />
                <p className="text-sm">No logo uploaded</p>
                <p className="text-xs">Default Hines Time logo will be used</p>
              </div>
            )}
          </div>

          {/* Upload and Action Buttons */}
          <div className="space-y-3">
            {/* Select File Button */}
            {!previewUrl && (
              <div>
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/gif,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading}
                />
                <label htmlFor="logo-upload" className="block">
                  <Button
                    type="button"
                    disabled={isUploading}
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('logo-upload').click();
                    }}
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {currentLogoUrl ? 'Change Logo' : 'Select Logo File'}
                  </Button>
                </label>
              </div>
            )}

            {/* Save and Cancel Buttons */}
            {previewUrl && (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isUploading}
                  className="h-12"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveLogo}
                  disabled={isUploading}
                  className="bg-green-600 hover:bg-green-700 h-12"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Save Logo
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Requirements */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-semibold mb-2">Logo Requirements:</p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li><Check className="w-3 h-3 inline mr-1" />Formats: PNG, JPG, SVG, GIF, or PDF</li>
              <li><Check className="w-3 h-3 inline mr-1" />Max size: 2MB</li>
              <li><Check className="w-3 h-3 inline mr-1" />Recommended: 200x200 to 400x400 pixels</li>
              <li><Check className="w-3 h-3 inline mr-1" />Transparent background recommended for PNG</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Document branding palette (Feb 2026, v11.2). Lives inside the same
        Company Branding view so admins find logo + colour controls in one
        place. */}
    <div className="mt-6">
      <DocumentBranding token={token} company={company} />
    </div>
    <div className="mt-6">
      <DocumentStyleCard token={token} company={company} />
      <ReportLayoutCard token={token} company={company} />
    </div>
    </>
  );
};

export default CompanyBranding;
